/**
 * /privacy: what Sparrow keeps, where, and what it sends, in plain words, true to the
 * code (docs/LAUNCH.md, "Questions for James" has what's still a legal call). Linked
 * from About Sparrow in ≡ and from Settings' "Count my plays".
 *
 * Two controls: "Count my plays" (the same switch as Settings, platform/consent.ts) and
 * "Clear Sparrow's data on this device", with its confirmation in the page (no dialog).
 */
import { useEffect, useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { setPageTitle } from '../app/page-title.ts';
import { useSettings } from '../app/settings.ts';
import { AppMenu } from '../components/AppMenu.tsx';
import { BackPill } from '../components/BackPill.tsx';
import { EVENT_NAMES } from '../platform/analytics.ts';
import { clearDeviceData, setUsageCounts } from '../platform/consent.ts';
import { platformKeys, platformStore } from '../platform/config.ts';
import { useNestMemory, useNestsVisible } from '../nests/store.ts';
import { pushState, turnOff, usePushState } from '../push/push.ts';
import { Collapsible, CollapsibleContent, CollapsibleTrigger, SwitchRow } from '../ui/index.ts';
import { EVENT_WORDS } from './privacy-copy.ts';
import '../styles/privacy.css';

/** Where questions go, until James fills it in. */
export const CONTACT = 'james@pixeltime.co.uk';
export const UPDATED = '25 October 2026';

interface PrivacyProps {
	todayKey?: string;
	/** Tests: instead of reloading the page after a clear. */
	onCleared?: () => void;
}

export function Privacy({ todayKey, onCleared }: PrivacyProps) {
	const { analytics } = useSettings();
	// Nests are behind a gate while they're tested (nests/store.ts): their section shows
	// once nests are visible to this player (dev mode, or in a nest).
	const nests = useNestsVisible();
	useEffect(() => {
		setPageTitle('Privacy');
	}, []);

	return (
		<div className="page privacy" data-testid="privacy">
			{/* The masthead is the page's banner, outside <main> (QA4-13); both scroll together. */}
			<div className="page-body page-scroll">
				<header className="page-mast">
					<BackPill mode="history" />
					<AppMenu screen="privacy" install={{ todayKey }} />
				</header>
				<main className="page-main">
					<h1 className="page-title">Privacy</h1>
					<p className="page-lede pv-lede">
						Sparrow has no accounts and no ads, and sets no cookies.
					</p>

					<section className="pv-sec">
						<h2 id="pv-device">On your device</h2>
						<p>
							Your games, streak, stats and settings are saved in this browser, on this phone or computer. You don’t sign in.
						</p>
						<p>
							The first time you open Sparrow it makes a <b>random id</b>, a string of letters and numbers. It isn’t your name, your email or
							anything about your phone. It’s how Sparrow tells one set of anonymous counts from another.
						</p>
					</section>

					<section className="pv-sec">
						<h2 id="pv-sent">What Sparrow sends</h2>
						<p>
							<b>Counts of how Sparrow is used</b>, tagged with the random id and the app’s version: when you open it, start or finish a
							puzzle, share, take a challenge, open the menu or change a setting. A finished puzzle sends your time, checks, reveals and best
							Flow. <b>Never your letters or answers</b>, and never a name or an email.
						</p>
						<p>
							<b>A copy of each solve</b> when you start and finish a puzzle: which puzzle, your time and those same numbers, and how each
							square went (solved, checked or revealed, not the letter in it). That’s what “Faster than 83% today” is worked out from, and
							what a challenge is made of.
						</p>
						<Collapsible className="pv-more">
							<CollapsibleTrigger className="pv-more-t">Every count Sparrow can send</CollapsibleTrigger>
							<CollapsibleContent>
								<ul>
									{EVENT_NAMES.map((name) => (
										<li key={name}>{EVENT_WORDS[name]}</li>
									))}
								</ul>
							</CollapsibleContent>
						</Collapsible>
						<ul className="mn-list sets pv-switch">
							<li>
								<SwitchRow
									label="Count my plays"
									checked={analytics}
									onToggle={() => setUsageCounts(!analytics)}
									testId="privacy-analytics"
								/>
							</li>
						</ul>
						<p>
							<b>With it off,</b> no counts are sent at all. Your solves stay on this device too, except when a challenge{nests ? ' or a nest' : ''}{' '}
							needs one: when you press Challenge a friend (the challenge is built from your solve, so the time can’t be faked), or finish a
							challenge someone sent you (so they can see how it went){nests ? ', or while you’re in a nest (below)' : ''}. “Faster than x%
							today” still shows; you just aren’t counted in it.
						</p>
					</section>

					<section className="pv-sec">
						<h2 id="pv-challenges">Challenges</h2>
						<p>
							A challenge is anonymous. It carries the puzzle, your time and how the board went, with no name anywhere, and anyone with the
							link can see that. Whoever made it is told how many people played it and how many beat them, never who.
						</p>
					</section>

					{nests && (
						<section className="pv-sec" data-testid="privacy-nests">
							<h2 id="pv-nests">If you join a nest</h2>
							<p>
								Sparrow keeps the name you give it, the nests you’re in and your times for each day’s puzzles, so the people in your nest can
								see them. Only your nest sees your name and times. Leave a nest and your name and activity in it are removed. Clear
								Sparrow’s data and the link to your nests goes too.
							</p>
							<p>
								Your solves are sent to Sparrow while you’re in a nest, even with “Count my plays” off, because that’s how your nest sees
								them.
							</p>
						</section>
					)}

					<section className="pv-sec" data-testid="privacy-push">
						<h2 id="pv-push">If you turn on notifications</h2>
						<p>
							Sparrow keeps a <b>push address</b> for this device, which your phone’s or browser’s maker gives it, and your <b>time zone</b>,
							so each notification arrives at the right time of your day. It also keeps which ones you chose and at what times, and the day
							you last played and your streak, so a reminder never arrives after you’ve played, and a note of what it sent today so your nests
							never send more than two. Turn them all off and the address, time zone, play day, streak and notes are deleted.
						</p>
						<p>
							Notifications travel through that maker’s push service (Apple, Google, Mozilla or Microsoft), encrypted so the service can’t
							read them. They never include answers or clues.
						</p>
					</section>

					<section className="pv-sec">
						<h2 id="pv-carry">Moving to your home screen</h2>
						<p>
							On an iPhone, and in some apps’ built-in browsers, Sparrow on your home screen starts with nothing saved. So when you open the
							“Add to home screen” guide, Sparrow sends a copy of your streak, stats, settings, challenges and today’s and yesterday’s games
							to its server under a <b>short code</b>, and the home-screen app collects it when it first opens. The code stops working
							after 30 minutes or three uses, whichever comes first, and the copy is deleted soon after. If the home-screen app was opened
							first and has saved things of its own, the code joins the two, and the home-screen app’s nests, games and notifications move to your random id. Sparrow’s old address (on
							workers.dev) does the same when you open it, to bring your streak across to sparrowword.com.
						</p>
					</section>

					<section className="pv-sec">
						<h2 id="pv-where">Where it lives</h2>
						<p>
							Sparrow’s server and its database run on <b>Railway</b>; the counts, solves, challenges and home-screen codes are kept there. The
							app itself is served by <b>Cloudflare</b>. Like any web host, they see your IP address in order to deliver the page, and may keep
							it for a short time in their logs. Sparrow doesn’t store it.
						</p>
						<p>
							Sparrow uses no third-party analytics, fonts or scripts. Nothing is sold or shared.
						</p>
					</section>

					<ClearData onCleared={onCleared} />

					<section className="pv-sec">
						<h2 id="pv-questions">Questions</h2>
						<p>Sparrow is made by Pixel Time Ltd, registered in England and Wales, company number 16084112.</p>
						<p>
							Email <a className="pv-mail" href={`mailto:${CONTACT}`}>{CONTACT}</a>. If you’d like the copies Sparrow has been sent deleted, include your random id (above), since that’s the only
							thing that ties them together.
						</p>
					</section>
					<p className="pv-foot">Last updated {UPDATED}.</p>
				</main>
			</div>
		</div>
	);
}

function readDeviceId(): string | null {
	const id = platformStore.get(platformKeys.device);
	return id && /^[0-9a-f-]{36}$/i.test(id) ? id : null;
}

/**
 * "Clear Sparrow's data on this device…": a quiet text button (it wipes the streak, so it
 * mustn't be the page's loudest thing, UX-072), then the confirmation in its place, with
 * the danger pill and "Keep it". For a nest member it says what stays behind, and that
 * notifications stop. Focus moves with the swap, in the handlers.
 */
function ClearData({ onCleared }: { onCleared?: (() => void) | undefined }) {
	const [asking, setAsking] = useState(false);
	const [deviceId] = useState(readDeviceId);
	const keepRef = useRef<HTMLButtonElement>(null);
	const startRef = useRef<HTMLButtonElement>(null);
	const askId = useId();
	const nests = useNestMemory().nests;
	const push = usePushState();

	const ask = () => {
		flushSync(() => setAsking(true));
		keepRef.current?.focus();
	};
	const keep = () => {
		flushSync(() => setAsking(false));
		startRef.current?.focus();
	};
	const clear = () => {
		// Notifications go too: the api forgets this device's push address (the request is
		// made, with this id, before the id is cleared).
		if (pushState().on) void turnOff({ forget: true });
		clearDeviceData();
		if (onCleared) onCleared();
		else window.location.replace('/');
	};

	return (
		<section className="pv-sec pv-clear">
			<h2 id="pv-clear">Your data on this device</h2>
			{deviceId && (
				<p>
					Your random id: <code className="pv-id">{deviceId}</code>
				</p>
			)}
			{!asking ? (
				<button ref={startRef} type="button" className="btn quiet pv-clear-btn" onClick={ask} data-testid="privacy-clear">
					Clear Sparrow’s data on this device…
				</button>
			) : (
				<div className="pv-confirm" role="group" aria-labelledby={askId}>
					<div id={askId}>
						<p>
							<b>Clear everything?</b> Your games, streak, stats, settings and random id go from this browser, and it can’t be undone.
							Copies already sent stay on the server, under an id nothing here points to any more.
						</p>
						{nests.length > 0 && (
							<p data-testid="privacy-clear-nests">
								You’ll stay in {listNames(nests.map((n) => n.name))} under your old name. Leave {nests.length === 1 ? 'it' : 'them'} first if
								you want your times removed.
							</p>
						)}
						{push.on && <p data-testid="privacy-clear-push">Notifications stop on this device too.</p>}
					</div>
					<div className="pv-acts">
						<button type="button" className="btn md danger fit" onClick={clear} data-testid="privacy-clear-confirm">
							Clear everything
						</button>
						<button ref={keepRef} type="button" className="btn md secondary fit" onClick={keep}>
							Keep it
						</button>
					</div>
				</div>
			)}
		</section>
	);
}

/** "Family", "Family and Work", "Family, Work and Book club". */
function listNames(names: readonly string[]): string {
	return names.length < 2 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
}
